import { useState, useEffect } from "react";
import axios from "axios";
import { Megaphone, Send, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_FROM = "ResumeAI Team";

export default function AdminAnnouncements() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: "", message: "", from_name: DEFAULT_FROM, link: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => { fetchAnnouncements(); }, []);

  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API}/admin/announcements`);
      setItems(res.data.announcements || []);
    } catch { toast.error("Failed to load announcements"); }
    finally { setLoading(false); }
  };

  const handleSend = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (!form.message.trim()) { toast.error("Message is required"); return; }
    if (!window.confirm("Send this announcement to EVERY user's notifications?")) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/admin/announcements`, form);
      toast.success(`Sent to ${res.data.recipients} user${res.data.recipients === 1 ? "" : "s"}!`);
      setForm({ title: "", message: "", from_name: form.from_name || DEFAULT_FROM, link: "" });
      fetchAnnouncements();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to send"); }
    finally { setSending(false); }
  };

  const handleRecall = async (id) => {
    if (!window.confirm("Recall this announcement? It will be removed from every user's notifications.")) return;
    try {
      await axios.delete(`${API}/admin/announcements/${id}`);
      setItems((prev) => prev.filter((a) => a.id !== id));
      toast.success("Announcement recalled");
    } catch { toast.error("Failed to recall"); }
  };

  const formatDate = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

  return (
    <div className="page-container fade-in" data-testid="admin-announcements-page">
      <div className="page-header">
        <div>
          <h1 style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}><Megaphone size={22} /> Announcements</h1>
          <p className="page-subtitle">Broadcast a message to every user's in-app notification bell.</p>
        </div>
      </div>

      <div className="admin-create-promo fade-in" data-testid="announcement-form">
        <div className="modal-row">
          <div className="modal-field" style={{ flex: 2 }}>
            <label>Title *</label>
            <input className="modal-input" placeholder="e.g. New feature: LinkedIn one-click import"
              value={form.title} maxLength={120}
              onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="announcement-title-input" />
          </div>
          <div className="modal-field">
            <label>From (sender name)</label>
            <input className="modal-input" placeholder={DEFAULT_FROM}
              value={form.from_name} maxLength={80}
              onChange={(e) => setForm({ ...form, from_name: e.target.value })} data-testid="announcement-from-input" />
          </div>
        </div>
        <div className="modal-row">
          <div className="modal-field" style={{ flex: 1 }}>
            <label>Message *</label>
            <textarea className="modal-input" rows={4} placeholder="Write your announcement…"
              value={form.message} maxLength={2000}
              onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="announcement-message-input" />
          </div>
        </div>
        <div className="modal-row">
          <div className="modal-field" style={{ flex: 1 }}>
            <label>Link (optional — clicking the notification opens this)</label>
            <input className="modal-input" placeholder="/app/optimize or https://…"
              value={form.link} maxLength={500}
              onChange={(e) => setForm({ ...form, link: e.target.value })} data-testid="announcement-link-input" />
          </div>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
          <button className="dash-cta-btn" onClick={handleSend} disabled={sending} data-testid="announcement-send-btn">
            <Send size={14} /> {sending ? "Sending…" : "Send to everyone"}
          </button>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "3rem" }}><div className="processing-spinner" /></div>
      ) : items.length === 0 ? (
        <div className="dash-empty-card" data-testid="admin-announcements-empty">
          <div className="dash-empty-icon"><Megaphone size={28} /></div>
          <h3>No announcements yet</h3>
          <p>Your first broadcast will appear here.</p>
        </div>
      ) : (
        <div className="admin-table-wrap" data-testid="admin-announcements-table">
          <table className="admin-table">
            <thead>
              <tr><th>Title</th><th>From</th><th>Recipients</th><th>Sent</th><th>Actions</th></tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} data-testid={`announcement-row-${a.id}`}>
                  <td>
                    <strong>{a.title}</strong>
                    <div style={{ fontSize: "0.78rem", color: "var(--text-secondary)", maxWidth: 380, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.body}</div>
                  </td>
                  <td>{a.from_name || "—"}</td>
                  <td><span className="admin-promo-users"><Users size={12} /> {a.recipients ?? 0}</span></td>
                  <td>{formatDate(a.created_at)}</td>
                  <td>
                    <button className="btn-icon danger" onClick={() => handleRecall(a.id)} data-testid={`announcement-recall-${a.id}`} title="Recall">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
